/** Радиус скругления столбца — тот же, что был у одиночной колонки. */
const RADIUS = 3

/** Нулевую корзину Apex рисует полоской толщиной в обводку — это не сегмент. */
const EMPTY_SEGMENT_PX = 1.5

/**
 * Прямоугольник со скруглением выборочных углов. Апекс такого не умеет: у него
 * либо все сегменты скруглены (`around`), либо «последний» — а какой сегмент
 * последний, в сгруппированном стеке он решает по-своему и в разных группах
 * по-разному (у депозитов скруглялась нижняя корзина, у выплат верхняя).
 */
function roundedRect(
  x: number,
  y: number,
  w: number,
  h: number,
  top: boolean,
  bottom: boolean
): string {
  const r = Math.max(0, Math.min(RADIUS, w / 2, h / 2))
  const rt = top ? r : 0
  const rb = bottom ? r : 0

  return (
    `M ${x} ${y + rt}` +
    (rt ? ` Q ${x} ${y} ${x + rt} ${y}` : ` L ${x} ${y}`) +
    ` H ${x + w - rt}` +
    (rt ? ` Q ${x + w} ${y} ${x + w} ${y + rt}` : '') +
    ` V ${y + h - rb}` +
    (rb ? ` Q ${x + w} ${y + h} ${x + w - rb} ${y + h}` : ` L ${x + w} ${y + h}`) +
    ` H ${x + rb}` +
    (rb ? ` Q ${x} ${y + h} ${x} ${y + h - rb}` : '') +
    ' Z'
  )
}

/**
 * Скругление стековых колонок по одному правилу на обе группы: **верхняя корзина
 * столбика скруглена сверху, нижняя — снизу, а если корзина в столбике одна, у
 * неё скруглены все четыре угла**. Середина стека — прямая.
 *
 * Делается после отрисовки (`mounted`/`updated`), потому что геометрия сегментов
 * известна только тогда; сами элементы Apex мы не подменяем — переписываем их
 * `d`, поэтому ховер, тултип и подсветка часа продолжают работать.
 *
 * Требует `plotOptions.bar.borderRadius: 0` — иначе Apex уже отдаст свои
 * (несогласованные) скругления, и bbox сегмента перестанет быть точным
 * прямоугольником.
 */
export function stackedBarRoundingEvents() {
  const draw = (ctx: any) => {
    const root: Element | null = ctx?.el ?? null
    if (!root || typeof root.querySelectorAll !== 'function') return

    const bars = Array.from(root.querySelectorAll('path.apexcharts-bar-area')) as SVGPathElement[]

    // Столбик — это сегменты с одинаковыми x и шириной. Нулевые корзины Apex всё
    // равно рисует полоской в пиксель — их отбрасываем, иначе «верхней» окажется
    // невидимая корзина, а единственная видимая получит прямые углы.
    const stacks = new Map<string, Array<{ el: SVGPathElement; box: DOMRect }>>()
    bars.forEach((el) => {
      const box = el.getBBox()
      if (box.height <= EMPTY_SEGMENT_PX || box.width <= 0) return
      const key = `${box.x.toFixed(1)}:${box.width.toFixed(1)}`
      const stack = stacks.get(key) ?? []
      stack.push({ el, box })
      stacks.set(key, stack)
    })

    stacks.forEach((stack) => {
      stack.sort((a, b) => a.box.y - b.box.y)
      stack.forEach(({ el, box }, i) => {
        el.setAttribute(
          'd',
          roundedRect(box.x, box.y, box.width, box.height, i === 0, i === stack.length - 1)
        )
      })
    })
  }

  return { mounted: draw, updated: draw, animationEnd: draw }
}
